/**
 * gdrive_read - Read content from a Google Drive file
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { googleDriveAdapter } from '../../services/integrations/adapters/GoogleDriveAdapter';
import { GoogleDriveErrorHandler } from '../../services/integrations/adapters/GoogleDriveErrorHandler';

export const gdrive_read: SimpleDocCommand = {
  id: 'gdrive_read',
  logicalName: 'gdrive_read',
  version: 'v1',
  
  description: `Read content from a file in Google Drive.

**PREREQUISITE:** File ID must be obtained from gdrive_search first.

**WHAT IT DOES:**
1. Reads content from specified Google Drive file
2. Automatically converts to readable format:
   - Google Docs → Markdown
   - Google Sheets → Structured JSON
   - Google Slides → Text outline
   - Text files → Plain text
3. Returns formatted content ready for analysis

**PARAMETERS:**
- fileId: The Google Drive file ID (from gdrive_search results)
- outputFormat: Optional format preference
  - 'auto': Automatically choose best format (default)
  - 'markdown': Force markdown output (for docs)
  - 'json': Force JSON output (for sheets)
  - 'text': Force plain text

**EXAMPLES:**

Read a Google Doc:
{ "fileId": "1abc123xyz..." }

Read a Google Sheet as JSON:
{ "fileId": "1def456uvw...", "outputFormat": "json" }

**SUPPORTED FILE TYPES:**
- ✅ Google Docs (converted to markdown)
- ✅ Google Sheets (converted to structured data)
- ✅ Google Slides (converted to text outline)
- ✅ Plain text files
- ❌ PDFs (not supported — use gdrive_import to bring into workspace)
- ❌ Images (use gdrive_import instead)
- ❌ Videos (not supported)
- ❌ Binary files (not supported)

**RESULTS:**
Returns:
- content: File content in specified format
- fileName: Original file name
- mimeType: Google Drive MIME type
- contentType: Output format (markdown/json/text)
- metadata: File metadata (size, modified date, etc.)

**WORKFLOW:**
Step 1: Search with gdrive_search
Step 2: Get file ID from results
Step 3: Read content with gdrive_read
Step 4: Analyze or import content

**LIMITS:**
- Max file size: 50MB (applies to plain text files; Google Workspace files have no enforced size)
- Images in docs are not extracted (only text)`,

  schema: {
    parameters: [
      {
        name: 'fileId',
        type: 'string',
        description: 'Google Drive file ID (from gdrive_search results)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim() === '') {
            return 'File ID is required';
          }
          return null;
        }
      },
      {
        name: 'outputFormat',
        type: 'string',
        description: 'Output format: auto | markdown | json | text',
        required: false,
        enum: ['auto', 'markdown', 'json', 'text']
      }
    ]
  },

  formatViewData: (result: StandardToolResult) => {
    const d = result?.data;
    return {
      type: 'gdrive_read',
      summary: result?.message || 'Read completed',
      message: result?.message,
      data: {
        fileName: d?.fileName,
        mimeType: d?.mimeType,
        contentType: d?.contentType,
        content: d?.content,
        metadata: d?.metadata,
      },
    };
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { fileId, outputFormat = 'auto' } = params;
    
    console.log('[gdrive_read] Reading file:', fileId);
    
    try {
      const { default: useEntityStore } = await import('../../stores/entityStore');
      const entities = useEntityStore.getState().entities;

      const driveIntegrations = entities.filter((e: any) =>
        (e.entityType === 'Integration.GoogleDrive' || e.entityType === 'Integration') &&
        e.skeleton?.integrationType === 'GoogleDrive' &&
        e.skeleton?.status === 'connected' &&
        !e.trashed
      );
      
      if (driveIntegrations.length === 0) {
        return {
          status: 'error',
          message: 'Google Drive not connected',
          error: { code: 'not_connected', category: 'validation', retryable: false },
          format: 'plaintext'
        };
      }

      const driveIntegration = driveIntegrations.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      
      const fileContent = await googleDriveAdapter.readFileContent(
        driveIntegration._id,
        fileId,
        outputFormat
      );
      
      console.log('[gdrive_read] Successfully read file:', fileContent.fileName);
      
      return {
        status: 'success',
        message: `Successfully read ${fileContent.fileName}`,
        data: {
          fileName: fileContent.fileName,
          mimeType: fileContent.mimeType,
          contentType: fileContent.contentType,
          content: fileContent.content,
          metadata: fileContent.metadata
        },
        format: 'json'
      };
      
    } catch (error: any) {
      const errorInfo = GoogleDriveErrorHandler.classifyError(error);
      
      // Map error categories to StandardToolResult categories
      const categoryMap: Record<string, 'validation' | 'execution' | 'timeout' | 'permission' | 'system' | 'cancelled'> = {
        'configuration': 'validation',
        'network': 'execution',
        'api': 'execution',
        'auth': 'permission',
        'validation': 'validation'
      };
      
      const standardCategory = categoryMap[errorInfo.category] || 'execution';
      
      console.error('[gdrive_read] Error:', {
        code: errorInfo.errorCode,
        category: errorInfo.category,
        standardCategory,
        retryable: errorInfo.retryable,
        originalError: error
      });
      
      return {
        status: 'error',
        message: errorInfo.userMessage,
        error: {
          code: errorInfo.errorCode,
          category: standardCategory,
          retryable: errorInfo.retryable,
          details: errorInfo.details
        },
        format: 'plaintext'
      };
    }
  }
};
